import React from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { Home, Shapes, Palette, Settings, GraduationCap, Hexagon } from 'lucide-react';
import { motion } from 'framer-motion';

const SidebarItem = ({ to, icon: Icon, label, color, borderColor }) => (
    <NavLink to={to} className={({ isActive }) => `
    flex flex-col items-center justify-center p-3 rounded-2xl transition-all duration-300
    border-2 ${borderColor}
    ${isActive
            ? 'bg-white shadow-lg scale-105'
            : 'bg-white/30 hover:bg-white/70 hover:scale-105'
        }
  `}>
        <div className={`p-2.5 rounded-xl ${color} text-white mb-1.5 shadow-sm`}>
            <Icon size={28} strokeWidth={2.5} />
        </div>
        <span className="font-bold text-gray-600 text-xs">{label}</span>
    </NavLink>
);

import { useAuth } from '../../context/AuthContext';

const Shell = () => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const isWhiteboard = location.pathname === '/whiteboard' || location.pathname === '/geoboard';

    return (
        <div className="flex h-screen bg-brand-blue/10 overflow-hidden font-sans">
            {/* Sidebar */}
            <nav className="w-24 md:w-32 bg-background-soft border-r-4 border-brand-blue/20 flex flex-col items-center py-4 shadow-xl z-20">
                <div className="mb-6 flex flex-col items-center">
                    <img 
                        src="/logo_planeta_primaria.svg" 
                        alt="Mundo Primaria" 
                        className="w-12 h-12 md:w-16 md:h-16 rounded-2xl shadow-md border-2 border-brand-yellow bg-brand-yellow" 
                    />
                </div>

                <div className="mb-4">
                    <button
                        onClick={logout}
                        title="Cambiar Estudiante"
                        className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full border-4 border-brand-orange flex items-center justify-center shadow-lg transform -rotate-6 transition-transform hover:rotate-0 hover:scale-110 overflow-hidden mx-auto"
                    >
                        {user?.avatar ? (
                            <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                            <GraduationCap className="text-brand-orange" size={24} />
                        )}
                    </button>
                    {user && (
                        <span className="text-[10px] font-bold text-gray-500 mt-1 block text-center truncate max-w-[80px] mx-auto">
                            {user.name}
                        </span>
                    )}
                </div>

                <div className="flex-1 w-full px-2 flex flex-col gap-3">
                    <SidebarItem to="/" icon={Home} label="Inicio" color="bg-brand-blue" borderColor="border-brand-blue" />
                    <SidebarItem to="/units" icon={Shapes} label="Aprender" color="bg-brand-green" borderColor="border-brand-green" />
                    <SidebarItem to="/whiteboard" icon={Palette} label="Pizarra" color="bg-brand-pink" borderColor="border-brand-pink" />
                    <SidebarItem to="/geoboard" icon={Hexagon} label="Geoplano" color="bg-violet-500" borderColor="border-violet-500" />
                </div>

                <div className="mt-auto pt-2">
                    <SidebarItem to="/settings" icon={Settings} label="Ajustes" color="bg-gray-400" borderColor="border-gray-400" />
                </div>
            </nav>

            {/* Main Content Area */}
            <main className="flex-1 relative overflow-hidden">
                {/* Decorative Background Blobs */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-brand-purple/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-yellow/20 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none" />

                <div className={`h-full overflow-y-auto ${isWhiteboard ? 'p-2' : 'p-4 md:p-8'}`}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className={`h-full mx-auto ${isWhiteboard ? 'max-w-full' : 'max-w-6xl'}`}
                    >
                        <Outlet />
                    </motion.div>
                </div>
            </main>
        </div>
    );
};

export default Shell;
